import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Clock, CheckCircle2, ArrowRight } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
import { categories } from '../data/products';
import { getRegionMeta } from '../utils/region';
export function Contact() {
  const region = getRegionMeta();
  const addressPlaceholder = `123 Main St, ${region.cities[0]}, WA`;
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const form = e.target as HTMLFormElement;
      const materialSelect = form.querySelector('select[class*="appearance-none"]') as HTMLSelectElement;
      // Estimated Yardage is now a free-text input (was previously a
      // restrictive dropdown with a wrong "5 yd minimum"). Read it by name.
      const yardageInput = form.querySelector('input[name="estimatedYardage"]') as HTMLInputElement | null;
      const addressInput = form.querySelector('input[name="deliveryAddress"]') as HTMLInputElement;
      const rc = region.code;
      const brandName = `${rc} Gravel`;
      const customerName = `${formData.firstName} ${formData.lastName}`.trim();
      // The EmailJS template renders {{message}} as the email body — keys
      // containing spaces (e.g. "Delivery Address") are not valid EmailJS
      // variables and never render, which is why these arrived blank. Compose
      // the full body here, exactly as the order flow does.
      const dispatchBody = ['New contact / quote request', '', `Name: ${customerName || '—'}`, `Email: ${formData.email || '—'}`, `Phone: ${formData.phone || '—'}`, `Delivery address: ${addressInput?.value || '—'}`, `Material: ${materialSelect?.value || '—'}`, `Estimated yardage: ${yardageInput?.value || '—'}`, '', 'Message:', formData.message || '—'].join('\n');
      await sendFormEmail({
        subject: `${rc}Gravel — New Contact / Quote Request`,
        to_name: customerName,
        from_name: brandName,
        message: dispatchBody,
        Name: customerName,
        'First Name': formData.firstName,
        'Last Name': formData.lastName,
        email: formData.email,
        _replyto: formData.email,
        _autoresponse: `Hi ${formData.firstName}! Thank you for reaching out to ${brandName}! We've received your message and will get back to you shortly.\n\nIf you need anything sooner, call us at (206) 619-3742.\n\n- The ${brandName} Team`,
        Phone: formData.phone,
        Material: materialSelect?.value || '',
        'Estimated Yardage': yardageInput?.value || '',
        'Delivery Address': addressInput?.value || '',
        Message: formData.message
      });
      setIsSubmitted(true);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        message: ''
      });
      setTimeout(() => {
        formRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Dark mode keeps the deeper, stepped-down palette; light
  // mode reverts to the original warm-cream tones so the page
  // reads bright and friendly in daylight while still feeling
  // grounded after dark.
  const inputClass = 'w-full bg-[#f2f4f0] dark:bg-[#2d2c2b] border border-stone-300 dark:border-[#3e3d3d] rounded-xl px-4 py-3 text-sm text-brand-charcoal dark:text-white placeholder-brand-muted dark:placeholder-stone-600 focus:ring-2 focus:ring-brand-orange focus:border-transparent outline-none transition-all';
  return <main className="min-h-[calc(100vh-64px)] bg-[#F2EFE8] dark:bg-[#2d2c2b] py-12 px-4 sm:px-6 lg:px-8 transition-colors duration-300 flex items-center justify-center" data-id="element-2278" data-mp="207odeju8bum6">
      <div className="max-w-6xl w-full mx-auto" data-id="element-2279" data-mp="1v7a2mu18u6kg">
        {/* Header */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="mb-10 text-center lg:text-left" data-id="element-2280" data-mp="2swd7w00s51oy">
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-stone-900 dark:text-white mb-3 tracking-tight" data-id="element-2281" data-mp="2j0s8m9n3ifiw">
            Contact & <span className="text-brand-orange" data-id="element-2282" data-mp="17k20gkz43dmn">Quotes</span>
          </h1>
          <p className="text-stone-600 dark:text-stone-300 text-sm md:text-base max-w-2xl mx-auto lg:mx-0 font-sub" data-id="element-2283" data-mp="13hylhmd8hqwm">
            Need a quote or have a question? Reach out directly or drop your
            project details below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-2284" data-mp="3j7f75et265hi">
          {/* Left Column: Form */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} ref={formRef} className="lg:col-span-2 bg-[#fcfbfa] dark:bg-[#3a3939] rounded-2xl p-6 md:p-8 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2285" data-mp="3o4fia44rlyfh">
            {isSubmitted ? <div className="h-full flex flex-col items-center justify-center text-center py-12" data-id="element-2286" data-mp="aqkh4zxd0fku">
                <div className="w-16 h-16 bg-brand-orange/10 rounded-full flex items-center justify-center mb-4" data-id="element-2287" data-mp="fqoz6udx1id6">
                  <CheckCircle2 className="w-8 h-8 text-brand-orange" data-id="element-2288" data-mp="2cj8jl726iyzk" />
                </div>
                <h3 className="font-heading text-2xl font-bold text-stone-900 dark:text-white mb-2" data-id="element-2289" data-mp="2hgqngj1cao0p">
                  Request Received
                </h3>
                <p className="text-stone-600 dark:text-stone-300 mb-6 max-w-sm text-sm" data-id="element-2290" data-mp="3kuaidg7bivv1">
                  Thanks for reaching out. We'll get back to you shortly with
                  pricing and availability.
                </p>
                <button onClick={() => setIsSubmitted(false)} className="px-6 py-2.5 bg-brand-orange hover:bg-brand-orangeHover text-white rounded-none font-bold text-sm transition-all" data-id="element-2291" data-mp="3otjc4l2cytql">
                  Send Another Request
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2292" data-mp="2kqgi5c5pq2i">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2293" data-mp="gcxxooqyj8g3">
                  <div data-id="element-2294" data-mp="cc4bbzt5y1pb">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2295" data-mp="cdxhldzv6jws">
                      First Name *
                    </label>
                    <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} required className={inputClass} data-id="element-2296" data-mp="ldua5qpdk2x4" />
                  </div>
                  <div data-id="element-2297" data-mp="14x9okcjp35nc">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2298" data-mp="19xxdmsod5cex">
                      Last Name *
                    </label>
                    <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} required className={inputClass} data-id="element-2299" data-mp="v3apjmex41zm" />
                  </div>
                </div>

                <div data-id="element-2300" data-mp="m7biu33ddomv">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2301" data-mp="qzcq1alokp61">
                    Email Address *
                  </label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className={inputClass} data-id="element-2302" data-mp="cc9u38d0aokt" />
                </div>

                <div data-id="element-2303" data-mp="398pjc3sh5j9">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2304" data-mp="3b1v0o2o1jpo">
                    Phone Number
                  </label>
                  <input type="tel" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} data-id="element-2305" data-mp="nreheyeoc06f" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2306" data-mp="isb9953eddu8">
                  <div data-id="element-2307" data-mp="drvwaen128cs">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2308" data-mp="dr447dsn2sr9">
                      Material Needed
                    </label>
                    <select className={inputClass + ' appearance-none'} data-id="element-2309" data-mp="4t26b747cg87">
                      <option value="" data-id="element-2310" data-mp="1k4kgucubm6">Select a material...</option>
                      {categories.map(cat => <optgroup key={cat.name} label={cat.name} data-id="element-2311" data-mp="3r7lbcd7aej20">
                          {cat.products.map(p => <option key={p.slug} value={p.name} data-id="element-2312" data-mp="3m8ievh5dhl5n">
                              {p.name} — ${p.pricePerYard}/yd
                            </option>)}
                        </optgroup>)}
                      <option value="Not Sure Yet" data-id="element-2313" data-mp="3d7slsl7hy8vm">Not Sure Yet</option>
                    </select>
                  </div>
                  <div data-id="element-2314" data-mp="1sqb35budac3m">
                    <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2315" data-mp="1sozy78p6qyax">
                      Estimated Yardage
                    </label>
                    <input type="text" name="estimatedYardage" className={inputClass} placeholder='e.g. "12 yards", "3 truckloads", "not sure"' data-id="element-2316" data-mp="21q6i7rr1mr8x" />
                  </div>
                </div>

                <div data-id="element-2317" data-mp="192angsl1b9es">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2318" data-mp="192tu3d58ibdi">
                    Delivery Address *
                  </label>
                  <input type="text" name="deliveryAddress" required className={inputClass} placeholder={addressPlaceholder} data-id="element-2319" data-mp="1izgey1xk92fj" />
                </div>

                <div data-id="element-2320" data-mp="1nrzs4xsc5vjt">
                  <label className="block text-sm font-medium text-brand-charcoal dark:text-stone-300 mb-2" data-id="element-2321" data-mp="1nshywocmjs5z">
                    Message *
                  </label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className={inputClass + ' resize-none'} data-id="element-2322" data-mp="v8t4b8w9fypa"></textarea>
                </div>

                <div className="pt-2" data-id="element-2323" data-mp="tvnlw241q86s">
                  <button type="submit" disabled={isSubmitting} className="w-full sm:w-auto inline-flex items-center justify-center px-8 py-3 bg-brand-button hover:bg-brand-buttonHover text-white rounded-none font-bold text-sm transition-all group disabled:opacity-60" data-id="element-2324" data-mp="tyrvxg46zy5d">
                    {isSubmitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-2325" data-mp="68xjgznscbln" /> : <>
                        Send Quote Request
                        <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-2326" data-mp="g490xpqkvwx3" />
                      </>}
                  </button>
                </div>
              </form>}
          </motion.div>

          {/* Right Column: Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="space-y-6" data-id="element-2327" data-mp="3ol3ugelhk4yi">
            {/* Contact Info Card */}
            <div className="bg-[#fcfbfa] dark:bg-[#3a3939] text-stone-900 dark:text-white rounded-2xl p-6 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2328" data-mp="21pp7596uhisa">
              <h3 className="font-heading text-lg font-bold mb-5" data-id="element-2329" data-mp="1rqgcsf82i25e">
                Get in Touch
              </h3>
              <div className="space-y-4" data-id="element-2330" data-mp="1mrd8iwzwvzyh">
                <a href="tel:206-619-3742" className="flex items-center group" data-id="element-2331" data-mp="1msep1gzp5enl">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-2332" data-mp="1hytww5ksbewb">
                    <Phone className="w-4 h-4 text-brand-orange" data-id="element-2333" data-mp="1908fidr1u5x4" />
                  </div>
                  <div data-id="element-2334" data-mp="140ccohu38kj5">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2335" data-mp="13zkf51gxkcq8">
                      Call Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2336" data-mp="z3lwluia9oxq">
                      (206) 619-3742
                    </p>
                  </div>
                </a>
                <a href="mailto:info@206gravel.com" className="flex items-center group" data-id="element-2337" data-mp="ueb6j8bjf0a5">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3 group-hover:bg-brand-orange/20 transition-colors" data-id="element-2338" data-mp="getbib7neqsc">
                    <Mail className="w-4 h-4 text-brand-orange" data-id="element-2339" data-mp="1844snivnr6g6" />
                  </div>
                  <div data-id="element-2340" data-mp="1d40xfkc6j1ws">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2341" data-mp="1d2g39vts1kzg">
                      Email Us
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2342" data-mp="za04x8btmb4v">
                      info@206gravel.com
                    </p>
                  </div>
                </a>
                <div className="flex items-center" data-id="element-2343" data-mp="1r161jesc1qpe">
                  <div className="w-10 h-10 bg-brand-orange/10 rounded-xl flex items-center justify-center mr-3" data-id="element-2344" data-mp="1vu8zdz457mbe">
                    <MapPin className="w-4 h-4 text-brand-orange" data-id="element-2345" data-mp="3gj36i57q25bi" />
                  </div>
                  <div data-id="element-2346" data-mp="3bk9vcjfwqqei">
                    <p className="text-xs text-stone-500 dark:text-stone-400 mb-0.5" data-id="element-2347" data-mp="3bl1e2toeotnd">
                      Location
                    </p>
                    <p className="font-medium text-sm text-stone-900 dark:text-white" data-id="element-2348" data-mp="3lbe95ftuk57r">
                      Bonney Lake, WA
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div className="bg-[#fcfbfa] dark:bg-[#3a3939] rounded-2xl p-6 border border-stone-200 dark:border-[#4a4949] shadow-lg" data-id="element-2349" data-mp="2sojpgl4ut9vc">
              <h3 className="font-heading text-lg font-bold text-stone-900 dark:text-white mb-4 flex items-center" data-id="element-2350" data-mp="32kwfn916x5lc">
                <Clock className="w-4 h-4 mr-2 text-brand-orange" data-id="element-2351" data-mp="mspgr6cx8za5" />
                Hours
              </h3>
              <div className="space-y-3 text-sm" data-id="element-2352" data-mp="htci7ocou3rq">
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#4a4949]" data-id="element-2353" data-mp="hwqjet9m86xl">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2354" data-mp="2shhmgpaszflf">
                    Mon - Fri
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-2355" data-mp="2wibb5kw2kyfd">
                    6:00 AM – 5:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center pb-2 border-b border-stone-200 dark:border-[#4a4949]" data-id="element-2356" data-mp="36hsgg17etaat">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2357" data-mp="3bftzwn8qzbmt">
                    Saturday
                  </span>
                  <span className="font-medium text-stone-900 dark:text-white" data-id="element-2358" data-mp="3g7vj6t4c22mc">
                    7:00 AM – 3:00 PM
                  </span>
                </div>
                <div className="flex justify-between items-center" data-id="element-2359" data-mp="3p8jwtmpte0yp">
                  <span className="text-stone-600 dark:text-stone-300" data-id="element-2360" data-mp="3p677gohq8obm">
                    Sunday
                  </span>
                  <span className="font-medium text-brand-orange" data-id="element-2361" data-mp="8670kh6ypyj7">Closed</span>
                </div>
              </div>
            </div>

            {/* Service Area */}
            <div className="px-1 pt-2" data-id="element-2362" data-mp="38xenbis8504">
              <h4 className="font-heading text-sm font-bold text-stone-900 dark:text-white mb-2" data-id="element-2363" data-mp="3uc3yn9p8afjd">
                Service Area
              </h4>
              <p className="text-xs text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-2364" data-mp="3qdd9ppux247c">
                Proudly serving King and Pierce counties — Seattle, Bellevue,
                Kirkland, Redmond, Renton, Kent, Auburn, Federal Way, Tacoma,
                Puyallup, Lakewood, Bonney Lake, and beyond — with reliable,
                same-day and next-day delivery. Not sure if you're in our
                delivery zone? Give us a call — we're happy to help.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </main>;
}